/**
 * Period Navigation Service using TimePeriods table
 * Replaces SQL Server /periods API with TimePeriods data
 */

import { supabase } from './client';

export interface PeriodNavigationItem {
  Period: string;        // Compatible with existing usePeriods interface
  ReportCount: number;   // Số lượng reports trong period
}

export interface PeriodPaginationMeta {
  page: number;
  limit: number;
  offset: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PeriodNavigationResponse {
  data: PeriodNavigationItem[];
  pagination: PeriodPaginationMeta;
}

class PeriodNavigationService {
  /**
   * Get periods for navigation with pagination
   * ✅ FIXED: Uses DISTINCT periods from REPORTS table (only periods with data)
   * ✅ Z-A sorting, 4 per page, unlimited pages
   */
  async getPeriods(
    page: number = 1, 
    limit: number = 4
  ): Promise<PeriodNavigationResponse> {
    const offset = (page - 1) * limit;

    try {
      // Step 1: Get DISTINCT periods from reports table with counts
      // ✅ FIX: Sort by created_on DESC to get newest reports first (includes W2540)
      const { data: periodsWithCounts, error: periodsError, count } = await supabase
        .from('reports')
        .select('period', { count: 'exact' })
        .not('period', 'is', null)
        .order('created_on', { ascending: false })
        .limit(1000); // Get 1000 most recent reports (enough for all periods)
      
      console.log('📊 Query reports for periods:', {
        totalReportsInDB: count,
        recordsFetched: periodsWithCounts?.length,
        hasW2540: periodsWithCounts?.some(r => r.period === 'W2540'),
        firstPeriod: periodsWithCounts?.[0]?.period,
        lastPeriod: periodsWithCounts?.[periodsWithCounts.length - 1]?.period
      });

      if (periodsError) {
        throw periodsError;
      }

      // Count reports by period and get unique periods
      const periodCounts: { [key: string]: number } = {};
      periodsWithCounts?.forEach(report => {
        const period = report.period;
        if (period) {
          periodCounts[period] = (periodCounts[period] || 0) + 1;
        }
      });

      // Get unique periods and sort Z-A
      const uniquePeriods = Object.keys(periodCounts).sort((a, b) => b.localeCompare(a));
      
      // DEBUG LOG
      console.log('🔍 periodNavigationService.getPeriods():');
      console.log('  - Page:', page);
      console.log('  - Limit:', limit);
      console.log('  - Offset:', offset);
      console.log('  - Unique periods count:', uniquePeriods.length);
      console.log('  - First 10 periods:', JSON.stringify(uniquePeriods.slice(0, 10)));
      console.log('  - Paginated range:', `${offset} to ${offset + limit}`);
      console.log('  - Paginated periods:', JSON.stringify(uniquePeriods.slice(offset, offset + limit)));
      
      // Apply pagination to the DISTINCT periods
      const totalCount = uniquePeriods.length;
      const totalPages = Math.ceil(totalCount / limit);
      const paginatedPeriods = uniquePeriods.slice(offset, offset + limit);

      // Transform to match original usePeriods interface
      const navigationData: PeriodNavigationItem[] = paginatedPeriods.map(period => ({
        Period: period,
        ReportCount: periodCounts[period] || 0,
      }));
      
      return {
        data: navigationData,
        pagination: {
          page,
          limit,
          offset,
          totalCount,
          totalPages,
          hasNextPage: page < totalPages,
          hasPrevPage: page > 1,
        },
      };

    } catch (error) {
      console.error('Failed to fetch periods navigation:', error);
      throw new Error(`Failed to fetch periods: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get period statistics from REPORTS table (only periods with data)
   * ✅ FIXED: Only count periods that have actual reports
   */
  async getPeriodStats(): Promise<{
    totalPeriods: number;
    totalReports: number;
    averageReportsPerPeriod: number;
    latestPeriod: string | null;
    oldestPeriod: string | null;
    periodTypes: string[];
  }> {
    try {
      // Get DISTINCT periods from reports table with counts
      const { data: reportsData, error: reportsError } = await supabase
        .from('reports')
        .select('period')
        .not('period', 'is', null);

      if (reportsError) {
        throw reportsError;
      }

      // Count reports by period
      const periodCounts: { [key: string]: number } = {};
      reportsData?.forEach(report => {
        const period = report.period;
        if (period) {
          periodCounts[period] = (periodCounts[period] || 0) + 1;
        }
      });

      // Get unique periods and sort Z-A
      const uniquePeriods = Object.keys(periodCounts).sort((a, b) => b.localeCompare(a));
      const totalReports = Object.values(periodCounts).reduce((sum, count) => sum + count, 0);

      return {
        totalPeriods: uniquePeriods.length,
        totalReports,
        averageReportsPerPeriod: uniquePeriods.length > 0 ? Math.round(totalReports / uniquePeriods.length) : 0,
        latestPeriod: uniquePeriods[0] || null,
        oldestPeriod: uniquePeriods[uniquePeriods.length - 1] || null,
        periodTypes: ['Weekly'], // From the data pattern (W3452, W3451, etc.)
      };

    } catch (error) {
      console.error('Failed to fetch period stats:', error);
      throw new Error(`Failed to fetch period stats: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get all available periods for dropdowns/selects
   * ✅ FIXED: Only periods that have reports (with data)
   */
  async getAllPeriods(): Promise<PeriodNavigationItem[]> {
    try {
      // Get DISTINCT periods from reports table
      const { data: reportsData, error } = await supabase
        .from('reports')
        .select('period')
        .not('period', 'is', null);

      if (error) {
        throw error;
      }

      // Get unique periods and sort Z-A
      const uniquePeriods = [...new Set(reportsData?.map(r => r.period) || [])]
        .filter(Boolean)
        .sort((a, b) => b.localeCompare(a));

      // Transform to navigation format
      return uniquePeriods.map(period => ({
        Period: period,
        ReportCount: 0, // Not needed for dropdown usage, but available if needed
      }));

    } catch (error) {
      console.error('Failed to fetch all periods:', error);
      throw new Error(`Failed to fetch all periods: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if a specific period exists (has reports)
   * ✅ FIXED: Check if period has data in reports table
   */
  async periodExists(periodId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('reports')
        .select('period')
        .eq('period', periodId)
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return !!data;

    } catch (error) {
      console.error('Failed to check period existence:', error);
      return false;
    }
  }
}

export const periodNavigationService = new PeriodNavigationService();